// frontend/src/pages/promotores/PromotoresListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function PromotoresListPage() {
  const { loading: authLoading, hasPermission } = useAuth();

  const canManage = hasPermission('config.promotores');
  const canView   = hasPermission('promotores.view') || canManage;
  const canCreate = hasPermission('promotores.create') || canManage;
  const canEdit   = hasPermission('promotores.edit') || canManage;
  const canDelete = hasPermission('promotores.delete') || canManage;

  const [promotores, setPromotores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchPromotores = async () => {
      setLoading(true);
      setError(null);

      if (!canView) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/promotores', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setPromotores(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchPromotores();
    return () => controller.abort();
  }, [authLoading, canView, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : promotores.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja desativar este promotor?')) {
      try {
        await api.delete(`/promotores/${id}`);
        setPromotores((prev) => prev.filter((p) => p.id !== id));
      } catch {
        alert('Ocorreu um erro ao desativar o promotor.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja reativar este promotor?')) {
      try {
        await api.patch(`/promotores/${id}/restore`);
        setPromotores((prev) => prev.filter((p) => p.id !== id));
      } catch {
        alert('Ocorreu um erro ao reativar o promotor.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="promotores-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="promotores-page">
        <LoadingSpinner text="Carregando promotores..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="promotores-page">
          <PermissionDeniedAlert
            requiredPermission="promotores.view"
            action="visualizar a lista de promotores"
          />
        </div>
      );
    }
    return (
      <div className="promotores-page">
        <AlertException error={error} fallback="Não foi possível carregar os promotores." />
      </div>
    );
  }

  return (
    <div className="promotores-page">
      <div className="content-header">
        <h1 className="content-title">Promotores</h1>
        <p className="content-subtitle">Gerenciamento de promotores de justiça</p>
      </div>

      <div className="card promotores-list-card">
        <div className="card-body">
          <div className="list-toolbar promotores-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="promotores-filters-group d-flex align-items-center gap-3">
              <div className="input-group promotores-search-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="promotores-status-filter d-flex align-items-center gap-2">
                <label htmlFor="promotoresStatusFilter" className="mb-0 promotores-status-label">Filtrar por:</label>
                <select
                  id="promotoresStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar promotores por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="promotores-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            {canCreate && (
              <Link to="/painel/promotores/novo" className="btn btn-primary">
                <i className="bi bi-plus-lg me-2"></i>
                Novo Promotor
              </Link>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {promotores.length > 0 ? (
                  promotores.map((p) => {
                    const isInactive = Boolean(p.deleted_at);

                    return (
                      <tr key={p.id}>
                        <td data-label="Nome">{p.nome}</td>
                        <td data-label="Status">{isInactive ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions promotores-table-actions d-flex gap-2" data-label="Ações">
                          {canEdit && !isInactive && (
                            <Link
                              to={`/painel/promotores/${p.id}/editar`}
                              className="btn btn-sm btn-outline-warning"
                              title="Editar"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}

                          {canDelete && !isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Desativar"
                              onClick={() => handleDelete(p.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}

                          {canDelete && isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(p.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">Nenhum promotor encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="promotores-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PromotoresListPage;
